import { getDomain } from '@sembo-private/utils';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { usePreferredTimeStore } from '@apps/main/modules/store/preferredTimeStore';

import { getDomainMarketOrigin } from '@libs/search-service-smart-search';

import type {
  ActivityDetailsTicketSelectedMetadata,
  BaseMetadata,
  ChangeHotelEventMetadata,
  CoreEventMetadata,
  EditTripModalOpenedLocationType,
  HotelDetailsEventMetadata,
  HotelSelectionFilterAppliedMetadata,
  HotelSelectionFilterClosedMetadata,
  HotelSelectionFilterOpenedMetadata,
  HotelSelectionFilterRemovedMetadata,
  HotelSelectionFilterResetMetadata,
  HotelSelectionInitiatedMetadata,
  TripOverviewBookClickedMetadata,
  TripOverviewHeaderActionsMetadata,
  TripOverviewInitiatedMetadata,
  TripOverviewPreferencesModalMetadata,
  TripOverviewUnavailableProductActionClickedMetadata,
  TripOverviewUnavailableProductRenderedMetadata,
} from '../../models';
import { StatsigEventType } from '../../models';
import { useBrowserStore } from '../browserStore';

import { logStatsigEventWrapper } from './logEventFn';

export const useStatsigEventsStore = defineStore('StatsigEvents', () => {
  const browserStore = useBrowserStore();
  const preferredTimeStore = usePreferredTimeStore();

  const baseMetadata = computed<BaseMetadata>(() => {
    const language = getDomain.getLanguageLocale();
    const domain = getDomainMarketOrigin();
    const dateMode = preferredTimeStore.preferredDay
      ? 'selectedDate'
      : 'flexible';
    const isDesktop = browserStore.isSmallDevice ? 'false' : 'true';

    return {
      language,
      domain,
      dateMode,
      isDesktop,
    };
  });

  // State
  const isAlternativeHotelOpened = ref<boolean>(false);

  // Actions: Core
  function logCoreDateChanged(metadata: CoreEventMetadata): void {
    logEvent(StatsigEventType.CoreDateChanged, undefined, { ...metadata });
  }

  function logCorePaxModalOpened(metadata: CoreEventMetadata): void {
    logEvent(StatsigEventType.CorePaxModalOpened, undefined, { ...metadata });
  }

  function logCorePaxUpdated(
    hasSelectedRoom: boolean,
    metadata: CoreEventMetadata
  ): void {
    logEvent(StatsigEventType.CorePaxUpdated, hasSelectedRoom.toString(), {
      ...metadata,
    });
  }

  function logCoreSearchByMovingMapToggle(
    isSearchByMovingMapActive: boolean,
    metadata: CoreEventMetadata
  ): void {
    logEvent(
      StatsigEventType.CoreSearchByMovingMapToggle,
      isSearchByMovingMapActive.toString(),
      { ...metadata }
    );
  }

  // Actions: Trip overview
  function logTripOverviewInitiated(
    metadata: TripOverviewInitiatedMetadata
  ): void {
    logEvent(StatsigEventType.TripOverviewInitiated, undefined, {
      totalAccomodation: metadata.totalAccomodation.toString(),
      totalFlight: metadata.totalFlight.toString(),
      totalFerry: metadata.totalFerry.toString(),
    });
  }

  function logTripOverviewNightsSelectorOpened(): void {
    logEvent(StatsigEventType.TripOverviewNightsSelectorOpened);
  }

  function logTripOverviewAddDestinationClicked(): void {
    logEvent(StatsigEventType.TripOverviewAddDestinationClicked);
  }

  function logTripOverviewBookClicked(
    metadata: TripOverviewBookClickedMetadata
  ): void {
    logEvent(StatsigEventType.TripOverviewBookClicked, undefined, {
      totalIngredients: metadata.totalIngredients.toString(),
      excludedIngredients: metadata.excludedIngredients.toString(),
      hasAccommodationProduct: metadata.hasAccommodationProduct.toString(),
      hasFerryProduct: metadata.hasFerryProduct.toString(),
      hasFlightProduct: metadata.hasFlightProduct.toString(),
      hasActivityProduct: metadata.hasActivityProduct.toString(),
      isMultiDestination: metadata.isMultiDestination.toString(),
    });
  }

  function logTripOverviewHotelChangeClicked(
    metadata: ChangeHotelEventMetadata
  ): void {
    logEvent(StatsigEventType.TripOverviewHotelChangeClicked, undefined, {
      ...metadata,
    });
  }

  function logTripOverviewTransportSelectorOpened(): void {
    logEvent(StatsigEventType.TripOverviewTransportSelectorOpened);
  }

  function logTripOverviewExploreActivitiesClicked(): void {
    logEvent(StatsigEventType.TripOverviewExploreActivitiesClicked);
  }

  function logTripOverviewAlternativeAccommodationGalleryImageSwipe(): void {
    logEvent(
      StatsigEventType.TripOverviewAlternativeAccommodationGalleryImageSwipe
    );
  }

  function logTripOverviewAccomodationGalleryImageSwipe(): void {
    logEvent(StatsigEventType.TripOverviewAccomodationGalleryImageSwipe);
  }

  function logTripOverviewActivityGalleryImageSwipe(): void {
    logEvent(StatsigEventType.TripOverviewActivityGalleryImageSwipe);
  }

  function logTripOverviewHotelToggle(value: boolean): void {
    logEvent(StatsigEventType.TripOverviewHotelToggle, value.toString());
  }

  function logTripOverviewOpenFullScreenMap(): void {
    logEvent(StatsigEventType.TripOverviewOpenFullScreenMap);
  }

  function logTripOverviewEditTripModalOpened(
    buttonLocation: EditTripModalOpenedLocationType
  ): void {
    logEvent(StatsigEventType.TripOverviewEditTripModalOpened, buttonLocation);
  }

  function logTripOverviewEditTripModalDestinationAdded(): void {
    logEvent(StatsigEventType.TripOverviewEditTripModalDestinationAdded);
  }

  function logTripOverviewEditTripModalDestinationChanged(): void {
    logEvent(StatsigEventType.TripOverviewEditTripModalDestinationChanged);
  }

  function logTripOverviewEditTripModalDestinationRearranged(): void {
    logEvent(StatsigEventType.TripOverviewEditTripModalDestinationRearranged);
  }

  function logTripOverviewDestinationTrashCanClicked(): void {
    logEvent(StatsigEventType.TripOverviewDestinationTrashCanClicked);
  }

  function logTripOverviewFlyFromButtonClicked(): void {
    logEvent(StatsigEventType.TripOverviewFlyFromButtonClicked);
  }

  function logTripOverviewUnavailableProductActionClicked(
    metadata: TripOverviewUnavailableProductActionClickedMetadata
  ): void {
    logEvent(
      StatsigEventType.TripOverviewUnavailableProductActionClicked,
      undefined,
      metadata
    );
  }

  function logTripOverviewUnavailableProductRendered(
    metadata: TripOverviewUnavailableProductRenderedMetadata
  ): void {
    logEvent(
      StatsigEventType.TripOverviewUnavailableProductRendered,
      undefined,
      metadata
    );
  }

  function logTripOverviewSeeAlternativeRoomsWhenNoAvail(): void {
    logEvent(StatsigEventType.TripOverviewSeeAlternativeRoomsWhenNoAvail);
  }

  function logTripOverviewAlternativeStaySelected(): void {
    logEvent(StatsigEventType.TripOverviewAlternativeStaySelected);
  }

  function logTripOverviewAlternativeRoomsDisplayed(): void {
    logEvent(StatsigEventType.TripOverviewAlternativeRoomsDisplayed);
  }

  function logTripOverviewAlternativeFlightDisplayed(): void {
    logEvent(StatsigEventType.TripOverviewAlternativeFlightDisplayed);
  }

  function logTripOverviewAlternativeFlightSelected(): void {
    logEvent(StatsigEventType.TripOverviewAlternativeFlightSelected);
  }

  function logTripOverviewHeaderActionClicked(
    metadata: TripOverviewHeaderActionsMetadata
  ): void {
    logEvent(
      StatsigEventType.TripOverviewHeaderActionClicked,
      undefined,
      metadata
    );
  }

  function logTripOverviewContinueWithSelectedDateModalOpened(): void {
    logEvent(StatsigEventType.TripOverviewContinueWithSelectedDateModalOpened);
  }

  function logTripOverviewContinueWithSelectedDateModalConfirmed(): void {
    logEvent(
      StatsigEventType.TripOverviewContinueWithSelectedDateModalConfirmed
    );
  }

  function logTripOverviewContinueWithSelectedDateModalCancelled(): void {
    logEvent(
      StatsigEventType.TripOverviewContinueWithSelectedDateModalCancelled
    );
  }

  function logTripOverviewPriceGuaranteeModalOpened(): void {
    logEvent(StatsigEventType.TripOverviewPriceGuaranteeModalOpened);
  }

  function logTripOverviewEditRoomClicked(): void {
    logEvent(StatsigEventType.TripOverviewEditRoomClicked);
  }

  function logTripOverviewAlternativeHotelsHotelSelectionCardClicked(): void {
    logEvent(
      StatsigEventType.TripOverviewAlternativeHotelsHotelSelectionCardClicked
    );
  }

  function logTripOverviewAlternativeHotelsCardClicked(): void {
    logEvent(StatsigEventType.TripOverviewAlternativeHotelsCardClicked);
  }

  function logTripOverviewAlternativeHotelsRendered(): void {
    logEvent(StatsigEventType.TripOverviewAlternativeHotelsRendered);
  }

  function logTripOverviewPreferencesModalOpened(
    metadata: TripOverviewPreferencesModalMetadata
  ): void {
    logEvent(
      StatsigEventType.TripOverviewPreferencesModalOpened,
      undefined,
      metadata
    );
  }

  function logTripOverviewActivityRemoved(): void {
    logEvent(StatsigEventType.TripOverviewActivityRemoved);
  }

  function logTripOverviewFlightAdded(): void {
    logEvent(StatsigEventType.TripOverviewFlightAdded);
  }

  function logTripOverviewFlightRemoved(): void {
    logEvent(StatsigEventType.TripOverviewFlightRemoved);
  }

  function logTripOverviewFerryAdded(): void {
    logEvent(StatsigEventType.TripOverviewFerryAdded);
  }

  function logTripOverviewFerryRemoved(): void {
    logEvent(StatsigEventType.TripOverviewFerryRemoved);
  }

  function logTripOverviewTripSummaryPanelOpened(): void {
    logEvent(StatsigEventType.TripOverviewTripSummaryPanelOpened);
  }

  // Actions: Hotel selection
  function logHotelSelectionInitiated(
    metadata: HotelSelectionInitiatedMetadata
  ): void {
    logEvent(StatsigEventType.HotelSelectionInitiated, undefined, {
      placeId: metadata.placeId.toString(),
      placeName: metadata.placeName,
      hotelResults: metadata.hotelResults.toString(),
    });
  }

  function logHotelSelectionFilterOpened(
    metadata: HotelSelectionFilterOpenedMetadata
  ): void {
    logEvent(StatsigEventType.HotelSelectionFilterOpened, undefined, {
      polygonId: metadata.polygonId.toString(),
      label: metadata.label,
      hotelResults: metadata.hotelResults.toString(),
    });
  }

  function logHotelSelectionFilterClosed(
    metadata: HotelSelectionFilterClosedMetadata
  ): void {
    logEvent(StatsigEventType.HotelSelectionFilterClosed, undefined, {
      polygonId: metadata.polygonId.toString(),
      label: metadata.label,
      hotelResults: metadata.hotelResults.toString(),
      eventPlacement: metadata.eventPlacement,
    });
  }

  function logHotelSelectionFilterApplied(
    metadata: HotelSelectionFilterAppliedMetadata
  ): void {
    logEvent(StatsigEventType.HotelSelectionFilterApplied, undefined, {
      filterName: metadata.filterName,
      filterGroup: metadata.filterGroup,
      filterDetail: metadata.filterDetail,
      polygonId: metadata.polygonId.toString(),
      pathname: metadata.pathname,
    });
  }

  function logHotelSelectionFilterRemoved(
    metadata: HotelSelectionFilterRemovedMetadata
  ): void {
    logEvent(StatsigEventType.HotelSelectionFilterRemoved, undefined, {
      filterName: metadata.filterName,
      filterGroup: metadata.filterGroup,
      filterDetail: metadata.filterDetail,
      polygonId: metadata.polygonId.toString(),
      pathname: metadata.pathname,
      eventPlacement: metadata.eventPlacement,
    });
  }

  function logHotelSelectionFilterReset(
    metadata: HotelSelectionFilterResetMetadata
  ): void {
    logEvent(StatsigEventType.HotelSelectionFilterReset, undefined, {
      polygonId: metadata.polygonId.toString(),
      label: metadata.label,
    });
  }

  function logHotelSelectionMapMarkerClicked(): void {
    logEvent(StatsigEventType.HotelSelectionMapMarkerClicked);
  }

  function logHotelSelectionListCardClicked(): void {
    logEvent(StatsigEventType.HotelSelectionListCardClicked);
  }

  function logHotelSelectionMapCardClicked(): void {
    logEvent(StatsigEventType.HotelSelectionMapCardClicked);
  }

  function logHotelSelectionBackButtonClicked(): void {
    logEvent(StatsigEventType.HotelSelectionBackButtonClicked);
  }

  function logHotelSelectionGalleryImageSwipe(): void {
    logEvent(StatsigEventType.HotelSelectionGalleryImageSwipe);
  }

  // Actions: Hotel details
  function logHotelDetailsInitiated(metadata: HotelDetailsEventMetadata): void {
    logEvent(StatsigEventType.HotelDetailsInitiated, undefined, {
      ...metadata,
    });
  }

  function logHotelDetailsReviewOpened(): void {
    logEvent(StatsigEventType.HotelDetailsReviewOpened);
  }

  function logHotelDetailsSelected(metadata: HotelDetailsEventMetadata): void {
    logEvent(StatsigEventType.HotelDetailsSelected, undefined, { ...metadata });
  }

  function logHotelDetailsStaySelected(
    metadata: HotelDetailsEventMetadata
  ): void {
    logEvent(StatsigEventType.HotelDetailsStaySelected, undefined, {
      ...metadata,
    });
  }

  function logHotelDetailsFullDescriptionModalOpened(
    metadata: HotelDetailsEventMetadata
  ): void {
    logEvent(
      StatsigEventType.HotelDetailsFullDescriptionModalOpened,
      undefined,
      { ...metadata }
    );
  }

  function logHotelDetailsAmenitiesModalOpened(
    metadata: HotelDetailsEventMetadata
  ): void {
    logEvent(StatsigEventType.HotelDetailsAmenitiesModalOpened, undefined, {
      ...metadata,
    });
  }

  function logHotelDetailsShowStaysButtonClicked(
    metadata: HotelDetailsEventMetadata
  ): void {
    logEvent(StatsigEventType.HotelDetailsShowStaysButtonClicked, undefined, {
      ...metadata,
    });
  }

  function logHotelDetailsRoomGalleryOpened(
    metadata: HotelDetailsEventMetadata
  ): void {
    logEvent(StatsigEventType.HotelDetailsRoomGalleryOpened, undefined, {
      ...metadata,
    });
  }

  function logHotelDetailsPriceGuaranteeModalOpened(
    metadata: HotelDetailsEventMetadata
  ): void {
    logEvent(
      StatsigEventType.HotelDetailsPriceGuaranteeModalOpened,
      undefined,
      {
        ...metadata,
      }
    );
  }

  function logHotelDetailsContinueWithSelectedRooms(
    metadata: HotelDetailsEventMetadata
  ): void {
    logEvent(
      StatsigEventType.HotelDetailsContinueWithSelectedRooms,
      undefined,
      {
        ...metadata,
      }
    );
  }

  function logHotelDetailsWithoutAvailActionTaken(
    metadata: HotelDetailsEventMetadata
  ): void {
    logEvent(StatsigEventType.HotelDetailsWithoutAvailActionTaken, undefined, {
      ...metadata,
    });
  }

  // Actions: Activity Selection
  function logActivitySelectionInitiated(): void {
    logEvent(StatsigEventType.ActivitySelectionInitiated);
  }

  function logActivitySelectionGalleryImageSwipe(): void {
    logEvent(StatsigEventType.ActivitySelectionGalleryImageSwipe);
  }

  function logActivitySelectionFilterOpened(): void {
    logEvent(StatsigEventType.ActivitySelectionFilterOpened);
  }

  // Actions: Activity details
  function logActivityDetailsTicketSelected(
    metadata: ActivityDetailsTicketSelectedMetadata
  ): void {
    logEvent(StatsigEventType.ActivityDetailsTicketSelected, undefined, {
      productCode: metadata.productCode,
    });
  }

  // Actions: Flights List
  function logFlightsListInitiated(): void {
    logEvent(StatsigEventType.FlightListInitiated);
  }

  function logFlightsListProductSelected(): void {
    logEvent(StatsigEventType.FlightListProductSelected);
  }

  // Actions: Ferry List
  function logFerryListInitiated(): void {
    logEvent(StatsigEventType.FerryListInitiated);
  }

  function logFerryListProductSelected(): void {
    logEvent(StatsigEventType.FerryListProductSelected);
  }

  // Actions: Telemetry
  function logMissingAmenityConfiguration(unknownAmenityName: string): void {
    logEvent(StatsigEventType.MissingAmenityConfiguration, unknownAmenityName);
  }

  function logTripOverviewInfiniteLoad(): void {
    logEvent(StatsigEventType.TripOverviewInfiniteLoad);
  }

  function logEvent(
    eventName: string,
    eventValue?: string | number | undefined,
    eventMetadata?: Record<string, string> | undefined
  ): void {
    logStatsigEventWrapper(
      eventName,
      eventValue,
      eventMetadata,
      baseMetadata.value
    );
  }

  return {
    // State
    isAlternativeHotelOpened,

    // Getters
    baseMetadata,

    // Actions: Core
    logCoreDateChanged,
    logCorePaxModalOpened,
    logCorePaxUpdated,
    logCoreSearchByMovingMapToggle,

    // Actions: Trip overview
    logTripOverviewInitiated,
    logTripOverviewNightsSelectorOpened,
    logTripOverviewAddDestinationClicked,
    logTripOverviewBookClicked,
    logTripOverviewHotelChangeClicked,
    logTripOverviewTransportSelectorOpened,
    logTripOverviewExploreActivitiesClicked,
    logTripOverviewHotelToggle,
    logTripOverviewOpenFullScreenMap,
    logTripOverviewEditTripModalOpened,
    logTripOverviewEditTripModalDestinationAdded,
    logTripOverviewEditTripModalDestinationChanged,
    logTripOverviewEditTripModalDestinationRearranged,
    logTripOverviewDestinationTrashCanClicked,
    logTripOverviewFlyFromButtonClicked,
    logTripOverviewUnavailableProductActionClicked,
    logTripOverviewUnavailableProductRendered,
    logTripOverviewAlternativeStaySelected,
    logTripOverviewSeeAlternativeRoomsWhenNoAvail,
    logTripOverviewAlternativeRoomsDisplayed,
    logTripOverviewAlternativeFlightDisplayed,
    logTripOverviewAlternativeFlightSelected,
    logTripOverviewHeaderActionClicked,
    logTripOverviewAlternativeAccommodationGalleryImageSwipe,
    logTripOverviewAccomodationGalleryImageSwipe,
    logTripOverviewActivityGalleryImageSwipe,
    logTripOverviewContinueWithSelectedDateModalOpened,
    logTripOverviewContinueWithSelectedDateModalConfirmed,
    logTripOverviewContinueWithSelectedDateModalCancelled,
    logTripOverviewPriceGuaranteeModalOpened,
    logTripOverviewEditRoomClicked,
    logTripOverviewAlternativeHotelsHotelSelectionCardClicked,
    logTripOverviewAlternativeHotelsCardClicked,
    logTripOverviewAlternativeHotelsRendered,
    logTripOverviewPreferencesModalOpened,
    logTripOverviewActivityRemoved,
    logTripOverviewFlightAdded,
    logTripOverviewFlightRemoved,
    logTripOverviewFerryAdded,
    logTripOverviewFerryRemoved,
    logTripOverviewTripSummaryPanelOpened,

    // Actions: Hotel selection
    logHotelSelectionInitiated,
    logHotelSelectionFilterOpened,
    logHotelSelectionFilterClosed,
    logHotelSelectionFilterApplied,
    logHotelSelectionFilterRemoved,
    logHotelSelectionFilterReset,
    logHotelSelectionMapMarkerClicked,
    logHotelSelectionListCardClicked,
    logHotelSelectionMapCardClicked,
    logHotelSelectionBackButtonClicked,
    logHotelSelectionGalleryImageSwipe,

    // Actions: Hotel details
    logHotelDetailsInitiated,
    logHotelDetailsReviewOpened,
    logHotelDetailsSelected,
    logHotelDetailsStaySelected,
    logHotelDetailsFullDescriptionModalOpened,
    logHotelDetailsAmenitiesModalOpened,
    logHotelDetailsShowStaysButtonClicked,
    logHotelDetailsRoomGalleryOpened,
    logHotelDetailsPriceGuaranteeModalOpened,
    logHotelDetailsContinueWithSelectedRooms,
    logHotelDetailsWithoutAvailActionTaken,

    // Actions: Activity Selection
    logActivitySelectionInitiated,
    logActivitySelectionGalleryImageSwipe,
    logActivitySelectionFilterOpened,

    // Actions: Activity details
    logActivityDetailsTicketSelected,

    // Actions: Flights List
    logFlightsListInitiated,
    logFlightsListProductSelected,

    // Actions: Ferry List
    logFerryListInitiated,
    logFerryListProductSelected,

    // Actions: Telemetry
    logMissingAmenityConfiguration,
    logTripOverviewInfiniteLoad,
  };
});
